import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  FinancialObligation,
  PaginatedFinancialObligations,
  FinancialObligationType,
  FinancialObligationStatus,
} from '@/types/financial-obligation'
import type {
  ObligationFormData,
  SettleObligationFormData,
} from '@/validators/financial-obligation'

export interface ListObligationParams {
  type?: FinancialObligationType
  status?: FinancialObligationStatus | 'OVERDUE'
  search?: string
  categoryId?: string
  startDate?: string
  endDate?: string
  page?: number
  perPage?: number
}

const KEYS = {
  list: (params: ListObligationParams) => ['financial-obligations', params] as const,
  detail: (id: string) => ['financial-obligations', id] as const,
}

export function useFinancialObligations(params: ListObligationParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api
        .get<PaginatedFinancialObligations>('/financial-obligations', { params })
        .then((r) => r.data),
  })
}

export function useFinancialObligation(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () =>
      api.get<FinancialObligation>(`/financial-obligations/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['financial-obligations'] })
}

export function useCreateObligation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ObligationFormData) =>
      api.post<FinancialObligation>('/financial-obligations', data).then((r) => r.data),
    onSuccess: () => invalidate(qc),
  })
}

export function useUpdateObligation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<ObligationFormData, 'type'>) =>
      api.put<FinancialObligation>(`/financial-obligations/${id}`, data).then((r) => r.data),
    onSuccess: () => invalidate(qc),
  })
}

export function useSettleObligation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: SettleObligationFormData) =>
      api
        .post<FinancialObligation>(`/financial-obligations/${id}/settle`, data)
        .then((r) => r.data),
    onSuccess: () => {
      invalidate(qc)
      qc.invalidateQueries({ queryKey: ['cash-transactions'] })
      qc.invalidateQueries({ queryKey: ['cash-accounts'] })
      qc.invalidateQueries({ queryKey: ['cash-flow-summary'] })
    },
  })
}

export function useCancelObligation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<FinancialObligation>(`/financial-obligations/${id}/cancel`).then((r) => r.data),
    onSuccess: () => invalidate(qc),
  })
}
